import { useLocale } from '@e2m-customer-portal/hooks';
import { InputFieldType } from '@e2m-customer-portal/shared';
import clsx from 'clsx';
import {
  addHours,
  eachHourOfInterval,
  endOfDay,
  isToday,
  isWeekend,
  startOfDay,
  startOfWeek,
  subHours,
} from 'date-fns';
import { Fragment, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

export interface ControlDeviceTablePlaceholderProps {
  daysInForecast: Date[];
  products: InputFieldType[];
}

export function ControlDeviceTablePlaceholder({
  daysInForecast,
  products,
}: Readonly<ControlDeviceTablePlaceholderProps>): ReactElement<ControlDeviceTablePlaceholderProps> {
  const { code: locale } = useLocale();
  const [t] = useTranslation();

  return (
    <tbody data-testid="device-table-placeholder">
      {eachHourOfInterval(
        {
          start: startOfDay(startOfWeek(daysInForecast[0], { weekStartsOn: 1 })),
          end: endOfDay(startOfWeek(daysInForecast[0], { weekStartsOn: 1 })),
        },
        { step: 4 },
      ).map((hourDate, rowIndex) => (
        <tr key={`row-${rowIndex}-${hourDate.toISOString()}`} className="group/last-row">
          <th
            className={clsx(
              'min-w-24 border-0 border-b border-solid pl-6 text-start text-sm font-normal leading-4',
              products.length >= 2
                ? 'border-blue-grey-100'
                : 'border-grey-100 group-last-of-type/last-row:border-blue-grey-100',
            )}
          >
            {`${hourDate.toLocaleTimeString(locale, {
              hour: '2-digit',
              minute: '2-digit',
            })} - ${addHours(hourDate, 4).toLocaleTimeString(locale, {
              hour: '2-digit',
              minute: '2-digit',
              hourCycle: 'h24',
            })}`}
          </th>
          <th className="p-0">
            <div className="flex flex-col">
              {products.map((product, productIndex) => {
                return (
                  <div
                    key={product}
                    className={clsx(
                      'border-0 border-b border-solid',
                      products.length >= 2 && productIndex === products.length - 1
                        ? 'border-blue-grey-100'
                        : 'border-grey-100',
                      {
                        'bg-grey-50': (productIndex + (products.length % 2 ? rowIndex : 0)) % 2 === 0,
                        'group-last-of-type/last-row:border-blue-grey-100':
                          products.length < 2 && productIndex === products.length - 1,
                      },
                    )}
                  >
                    <div className="min-w-[calc(4rem-2px)] border-0 border-l border-solid border-grey-100 px-1 text-start text-sm font-normal leading-6">
                      {t(`SHARED_PRODUCT_${product}`)}
                    </div>
                  </div>
                );
              })}
            </div>
          </th>
          {daysInForecast.map((date, index) => (
            <Fragment key={`slice-${index}-${new Date(hourDate.setHours(hourDate.getHours())).toISOString()}`}>
              <td className="relative p-0">
                <div className="flex flex-col">
                  {products.map((product, productIndex) => (
                    <div
                      key={product}
                      className={clsx(
                        'group/cell relative box-border border-0 border-b border-solid text-center outline-2 -outline-offset-1',
                        products.length >= 2 && productIndex === products.length - 1
                          ? 'border-blue-grey-100'
                          : 'border-grey-100',
                        {
                          'bg-grey-50': (productIndex + (products.length % 2 ? rowIndex : 0)) % 2 === 0,
                          'group-last-of-type/last-row:border-blue-grey-100':
                            products.length < 2 && productIndex === products.length - 1,
                        },
                      )}
                    >
                      <div
                        className={clsx('box-border border-0 border-l border-solid border-grey-100 text-sm', {
                          'bg-blue-grey-200/5': isWeekend(subHours(date, date.getTimezoneOffset() / 60)),
                        })}
                      >
                        <div
                          className={clsx(
                            'box-border flex h-6 w-full items-center justify-center gap-1 border-0 border-l border-solid border-grey-100',
                            { 'bg-green-50/40': isToday(date) },
                          )}
                        >
                          <div className="animate-loading-1 aspect-square w-1.5 animate-pulse rounded-full bg-black/10" />
                          <div className="animate-loading-2 aspect-square w-1.5 animate-pulse rounded-full bg-black/10" />
                          <div className="animate-loading-3 aspect-square w-1.5 animate-pulse rounded-full bg-black/10" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </td>
              {index === daysInForecast.length - 1 && (
                <td className="relative p-0">
                  <div className="absolute inset-0 flex flex-col">
                    {products.map((product, productIndex) => (
                      <div
                        key={product}
                        className={clsx(
                          'group/cell relative box-border flex-auto border-0 border-b border-solid',
                          products.length >= 2 && productIndex === products.length - 1
                            ? 'border-blue-grey-100'
                            : 'border-grey-100',
                          {
                            'bg-grey-50': (productIndex + (products.length % 2 ? rowIndex : 0)) % 2 === 0,
                            'group-last-of-type/last-row:border-blue-grey-100':
                              products.length < 2 && productIndex === products.length - 1,
                          },
                        )}
                      >
                        <div
                          className={clsx('absolute inset-0', {
                            'bg-blue-grey-200/5': isWeekend(subHours(date, date.getTimezoneOffset() / 60)),
                          })}
                        />
                      </div>
                    ))}
                  </div>
                </td>
              )}
            </Fragment>
          ))}
        </tr>
      ))}
    </tbody>
  );
}
